export type PublicService = {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string;
  starting_price: number;
  price_suffix: string | null;
  duration_minutes: number | null;
  duration_label: string;
  deposit_percent: number;
  status: 'published' | 'draft' | 'unavailable';
  sort_order: number;
  is_featured: boolean;
};

export type PublicPortfolioItem = {
  id: string;
  title: string;
  slug: string;
  category: string;
  caption: string;
  alt_text: string;
  image_path: string | null;
  image_url: string;
  starting_price: number | null;
  price_label: string | null;
  shape: string | null;
  length: string | null;
  finish: string | null;
  status: 'published' | 'draft' | 'archived';
  is_featured: boolean;
  sort_order: number;
};

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://dnxdqasmzdzuhqflwxpo.supabase.co';
export const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'sb_publishable_5nGCHxqax6NGzlzHe_BmYA_heezxVP4';

async function publicTableRequest<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    signal,
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Supabase request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export function getPublishedServices(signal?: AbortSignal) {
  const columns = [
    'id',
    'slug',
    'name',
    'category',
    'description',
    'starting_price',
    'price_suffix',
    'duration_minutes',
    'duration_label',
    'deposit_percent',
    'status',
    'sort_order',
    'is_featured',
  ].join(',');

  return publicTableRequest<PublicService[]>(
    `services?select=${columns}&status=eq.published&order=sort_order.asc`,
    signal,
  );
}

export function getPublishedPortfolio(signal?: AbortSignal) {
  const columns = [
    'id',
    'title',
    'slug',
    'category',
    'caption',
    'alt_text',
    'image_path',
    'image_url',
    'starting_price',
    'price_label',
    'shape',
    'length',
    'finish',
    'status',
    'is_featured',
    'sort_order',
  ].join(',');

  return publicTableRequest<PublicPortfolioItem[]>(
    `portfolio_items?select=${columns}&status=eq.published&order=sort_order.asc`,
    signal,
  );
}

export function formatServicePrice(service: Pick<PublicService, 'starting_price' | 'price_suffix'>) {
  const suffix = service.price_suffix ? ` ${service.price_suffix}` : '';
  return `From ₦${service.starting_price.toLocaleString('en-NG')}${suffix}`;
}

export function formatPortfolioPrice(item: Pick<PublicPortfolioItem, 'starting_price' | 'price_label'>) {
  if (item.price_label) return item.price_label;
  if (item.starting_price === null) return 'Quote after review';
  return `From ₦${item.starting_price.toLocaleString('en-NG')}`;
}
